import { motion } from "framer-motion"; // for smooth animation
import {
  FaUsers,
  FaRegLightbulb,
  FaChartBar,
  FaClipboardList,
  FaHandsHelping,
} from "react-icons/fa";

const features = [
  {
    icon: <FaRegLightbulb />,
    title: "Innovation",
    description: "Tailored solutions for social impact.",
  },
  {
    icon: <FaChartBar />,
    title: "Research & Policy Analysis",
    description: "In-depth insights and strategic planning.",
  },
  {
    icon: <FaClipboardList />,
    title: "Program Design",
    description: "Building impactful programs for diverse needs.",
  },
  {
    icon: <FaHandsHelping />,
    title: "Collaboration",
    description: "Empowering clients through partnerships.",
  },
  {
    icon: <FaUsers />,
    title: "Training & Assessment",
    description: "Equipping teams with the tools for change.",
  },
];

const AboutUsSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Header */}
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-4xl font-heading font-bold text-primary mb-8"
        >
          About Us
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-lg text-gray-700 mb-12 font-body"
        >
          At{" "}
          <span className="text-secondary font-semibold">
            Impact Together Consultancy Services
          </span>
          , we partner with change-makers across the spectrum — from government
          bodies and nonprofits to corporates, philanthropists, and
          mission-driven enterprises.
        </motion.p>

        {/* Custom styles for glassmorphism and card border */}
        <style>{`
          .glass-card {
            background: linear-gradient(135deg, rgba(255,255,255,0.85) 60%, rgba(186,230,253,0.7) 100%);
            backdrop-filter: blur(10px) saturate(180%);
            border-radius: 1.5rem;
            box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.10);
            border: 1.5px solid rgba(56,189,248,0.18);
            transition: transform 0.3s, box-shadow 0.3s, background-color 0.3s;
          }

          .glass-card:hover {
            box-shadow: 0 12px 36px 0 rgba(99,102,241,0.18);
            transform: translateY(-6px) scale(1.05);
            background-color: rgba(56,189,248,0.1); /* Light blue hover */
          }

          .card-icon {
            transition: transform 0.3s, color 0.3s;
          }

          .glass-card:hover .card-icon {
            color: #16A7C4; /* Secondary color on hover */
            transform: scale(1.1); /* Zoom-in on hover */
          }
        `}</style>

        {/* Services / Features List */}
        <div className="flex flex-col items-center gap-8">
          {/* Top row: 3 cards */}
          <div className="flex flex-col sm:flex-row justify-center gap-8 w-full max-w-4xl mx-auto">
            {features.slice(0, 3).map((item, index) => (
              <motion.div
                key={index}
                className="glass-card flex-1 min-w-0 sm:min-w-[220px] max-w-xs p-8 mx-auto cursor-pointer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 + index * 0.2 }}
              >
                <div className="flex items-center justify-center mb-4">
                  <div className="text-5xl text-primary card-icon">
                    {item.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-primary mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-700">{item.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Bottom row: 2 cards */}
          <div className="flex flex-col sm:flex-row justify-center gap-8 w-full max-w-2xl mx-auto">
            {features.slice(3).map((item, index) => (
              <motion.div
                key={index + 3}
                className="glass-card flex-1 min-w-[220px] max-w-xs p-8 mx-auto cursor-pointer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.9 + index * 0.2 }}
              >
                <div className="flex items-center justify-center mb-4">
                  <div className="text-5xl text-primary card-icon">
                    {item.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-primary mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-700">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Collapsible Content */}
        <div className="mt-12">
          <motion.p
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-lg text-gray-700 max-w-2xl mx-auto font-body"
          >
            We offer strategic consulting, research, program design, and
            implementation support — all tailored to your unique context and
            goals. Whether you're building a policy, scaling a social
            initiative, or strengthening community systems, our team is here to
            help you achieve lasting, measurable impact.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default AboutUsSection;
